import type { DocArticle } from './docs';

/**
 * Searching the documentation, in the browser, over the articles that are
 * loaded on entry to the docs.
 *
 * There is no search server here and there does not need to be one. Every
 * article's frontmatter *and* body is imported at build time (see
 * ./docs-content.ts), so the whole corpus is already
 * sitting in memory by the time anyone types into the box. A query is a pass
 * over an array, which is faster than a network round trip would be even if we
 * had somewhere to send it.
 *
 * The index is built lazily and cached per article, so a visitor who never
 * searches never pays for it. Notably it reads `article.markdown` and never
 * `article.html`: touching the HTML would render every article's Markdown on
 * the first keystroke, which is exactly the cost the memoized getter in
 * ./docs.ts exists to avoid.
 */

/** A run of text, flagged as matched or not, so the page can mark the matches without dangerouslySetInnerHTML. */
export interface SearchSegment {
  text: string;
  match: boolean;
}

export interface DocSearchResult {
  article: DocArticle;
  /** Higher is a better answer. Ordering only — the number is not shown to anyone. */
  score: number;
  /** The title, split so the matched words can be marked. */
  title: SearchSegment[];
  /** A line of context from wherever the words were found, matches marked. */
  snippet: SearchSegment[];
}

/**
 * Below this, a query matches so much of the corpus that the results are
 * noise. It is also the length at which someone is still typing the first word.
 */
export const MIN_QUERY_LENGTH = 2;

/** At most this many words are searched; the rest of a pasted paragraph is ignored. */
const MAX_TERMS = 6;

const SNIPPET_LENGTH = 190;
const SNIPPET_LEAD = 60;

/**
 * Composed form, applied to both sides before anything is folded or compared.
 *
 * "ubicación" can be written two ways that look identical — one code point, or an
 * "i" followed by a combining acute — and text pasted out of a filename or an
 * older document is often the second. `fold` below cannot strip a combining
 * mark that stands alone, because dropping it would shorten the string and
 * shift every index after it, so a decomposed query would keep its accent
 * while the article lost one and the two would never meet. Composing first
 * makes them the same string before that question can arise.
 */
const composed = (text: string) => text.normalize('NFC');

/**
 * Lowercases and strips accents **without changing the string's length**, so an
 * index into the folded text is the same index in the original.
 *
 * That constraint is what lets the snippet quote the article as written —
 * accents, capitals and all — while the matching itself is accent-blind, which
 * matters on a bilingual site: somebody searching the Spanish articles for
 * "autenticación" types it without the accent about half the time, and nobody types
 * "ubicación" with one on a phone keyboard.
 *
 * Feed it `composed` text (both callers do): on anything else a lone combining
 * mark survives folding, which is a match nobody can type their way to.
 */
export function fold(text: string): string {
  let folded = '';
  for (const character of text) {
    const candidate = character
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    // A fold that changes the length (ẞ → ss, İ → i̇) would shift every index
    // after it, so those few characters are left as they are.
    folded += candidate.length === character.length ? candidate : character;
  }
  return folded;
}

/**
 * Words that carry no information about which article somebody wants, in both
 * the languages this site is written in.
 *
 * They are dropped because every word in a query has to appear in an article
 * for it to match at all: without this, "how do I take a deposit" is a query
 * for six words, five of which are in every article ever written, and the one
 * that mattered is diluted to a sixth of the score. They are dropped *only when
 * something is left*, so a search for "the" still searches for "the".
 */
const STOPWORDS = new Set(
  (
    'a an and are as at be but by can do does for from has have how i if in into is it its me my not of on or our so ' +
    'that the their them then there these they this to up was we what when where which who why will with you your ' +
    'al algo como con cual cuando de del desde donde el ella ellos en entre era es esa ese eso esta este esto ha hay ' +
    'la las lo los mas mi mucho muy no nos o para pero por que se ser si sin sobre son su sus te tu un una uno y ya'
  ).split(' '),
);

/** The words a query is looking for: folded, de-duplicated, longest first so the best match anchors the snippet. */
export function docSearchTerms(query: string): string[] {
  const all = [
    ...new Set(
      fold(composed(query))
        .split(/[^\p{L}\p{N}]+/u)
        .filter((term) => term.length > 0),
    ),
  ];
  // A single letter matches most of the alphabet's worth of words; it is only
  // ever searched for if it is all there is.
  const words = all.filter((term) => term.length > 1);
  const usable = words.length > 0 ? words : all;
  const meaningful = usable.filter((term) => !STOPWORDS.has(term));
  return (meaningful.length > 0 ? meaningful : usable).sort((a, b) => b.length - a.length).slice(0, MAX_TERMS);
}

/**
 * The article as prose: Markdown syntax removed, so a search for "docs" does
 * not match every image path and a snippet never quotes a URL at the reader.
 * Link and image text is kept — it is written for people and is as much a part
 * of the article as the paragraph around it.
 */
export function markdownToPlainText(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/^\s{0,3}(?:[-*+]|\d+[.)])\s+/gm, '')
    .replace(/^\s{0,3}[-*_]{3,}\s*$/gm, ' ')
    .replace(/\|/g, ' ')
    .replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, '$2')
    .replace(/(\*|_)(?=\S)([^*_]*?\S)\1/g, '$2')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

interface IndexedArticle {
  /**
   * The article's own text, composed. Everything shown in a result is sliced
   * out of these rather than off the article, so the text and the folded copy
   * an index was found in are always the same string.
   */
  title: string;
  description: string;
  body: string;
  foldedBody: string;
  foldedTitle: string;
  foldedDescription: string;
}

/**
 * One entry per article, built the first time it is searched and kept for as
 * long as the article object lives. The articles are module-level constants, so
 * in practice this is built once per session and only if somebody searches.
 */
const index = new WeakMap<DocArticle, IndexedArticle>();

function indexed(article: DocArticle): IndexedArticle {
  let entry = index.get(article);
  if (!entry) {
    const title = composed(article.title);
    const description = composed(article.description);
    const body = composed(markdownToPlainText(article.markdown));
    entry = {
      title,
      description,
      body,
      foldedBody: fold(body),
      foldedTitle: fold(title),
      foldedDescription: fold(description),
    };
    index.set(article, entry);
  }
  return entry;
}

/**
 * At this length and shorter, a term is only matched where a word starts.
 *
 * A search for `AI` — a whole section of the documentation — otherwise matches
 * p**ai**d, em**ai**l and ag**ai**n, which is every article ever written;
 * Spanish `IA` is worse, because it ends half the language's nouns. Anything
 * longer is specific enough that matching inside a word is a feature rather
 * than a defect: `permis` should find "permissions" while it is still being typed.
 */
const WORD_ANCHORED_LENGTH = 3;

const WORD_CHARACTER = /[\p{L}\p{N}]/u;

const startsWord = (folded: string, at: number) => at === 0 || !WORD_CHARACTER.test(folded[at - 1]!);

/** Where `term` next occurs in `folded` at or after `from`, or -1. Short terms only count at the start of a word. */
function indexOfTerm(folded: string, term: string, from = 0): number {
  let at = folded.indexOf(term, from);
  if (term.length > WORD_ANCHORED_LENGTH) return at;
  while (at !== -1 && !startsWord(folded, at)) {
    at = folded.indexOf(term, at + 1);
  }
  return at;
}

const containsTerm = (folded: string, term: string) => indexOfTerm(folded, term) !== -1;

function occurrences(haystack: string, needle: string): number {
  let count = 0;
  let from = 0;
  for (;;) {
    const at = indexOfTerm(haystack, needle, from);
    if (at === -1) return count;
    count += 1;
    from = at + needle.length;
  }
}

/** Every span of `folded` covered by one of the terms, overlaps merged, in order. */
function matchRanges(folded: string, terms: string[]): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  for (const term of terms) {
    let from = 0;
    for (;;) {
      const at = indexOfTerm(folded, term, from);
      if (at === -1) break;
      ranges.push([at, at + term.length]);
      from = at + term.length;
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);

  const merged: Array<[number, number]> = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && range[0] <= last[1]) {
      last[1] = Math.max(last[1], range[1]);
    } else {
      merged.push([...range]);
    }
  }
  return merged;
}

/** Splits `text` into matched and unmatched runs. `folded` must be `fold(text)` — same length, same indices. */
export function markMatches(text: string, terms: string[], folded = fold(text)): SearchSegment[] {
  const ranges = matchRanges(folded, terms);
  if (ranges.length === 0) return text ? [{ text, match: false }] : [];

  const segments: SearchSegment[] = [];
  let at = 0;
  for (const [start, end] of ranges) {
    if (start > at) segments.push({ text: text.slice(at, start), match: false });
    segments.push({ text: text.slice(start, end), match: true });
    at = end;
  }
  if (at < text.length) segments.push({ text: text.slice(at), match: false });
  return segments;
}

/** Widens `[start, end)` out to the surrounding whitespace, so a snippet never begins mid-word. */
function toWordBoundaries(text: string, start: number, end: number): [number, number] {
  let from = start;
  if (from > 0) {
    const space = text.indexOf(' ', from);
    from = space === -1 || space > from + 20 ? from : space + 1;
  }
  let to = end;
  if (to < text.length) {
    const space = text.lastIndexOf(' ', to);
    to = space <= from ? to : space;
  }
  return [from, to];
}

/** A window of `text` around the first term that appears in it, matches marked. Undefined when nothing matches. */
function snippetFrom(text: string, folded: string, terms: string[]): SearchSegment[] | undefined {
  const first = terms
    .map((term) => indexOfTerm(folded, term))
    .filter((at) => at !== -1)
    .sort((a, b) => a - b)[0];
  if (first === undefined) return undefined;

  const [from, to] = toWordBoundaries(
    text,
    Math.max(0, first - SNIPPET_LEAD),
    Math.min(text.length, first + SNIPPET_LENGTH - SNIPPET_LEAD),
  );
  const segments = markMatches(text.slice(from, to), terms, folded.slice(from, to));
  if (from > 0) segments.unshift({ text: '… ', match: false });
  if (to < text.length) segments.push({ text: ' …', match: false });
  return segments;
}

/** The opening of an article's description, for a query that only matched its title. */
function leadingSummary(summary: string): SearchSegment[] {
  if (summary.length <= SNIPPET_LENGTH) return [{ text: summary, match: false }];
  const [, to] = toWordBoundaries(summary, 0, SNIPPET_LENGTH);
  return [{ text: `${summary.slice(0, to)} …`, match: false }];
}

/**
 * The articles matching `query`, best answer first.
 *
 * Every word has to appear somewhere in an article for it to match at all —
 * "delete workspace" should not return every article mentioning a workspace — and where
 * each word appears is what orders the results: a word in the title says the
 * article is about the thing, the same word four paragraphs down says it was
 * mentioned. A whole-phrase match in the title outranks both.
 */
export function searchDocArticles(articles: DocArticle[], query: string): DocSearchResult[] {
  const trimmed = query.trim();
  if (trimmed.length < MIN_QUERY_LENGTH) return [];
  const terms = docSearchTerms(trimmed);
  if (terms.length === 0) return [];
  const phrase = fold(composed(trimmed)).replace(/\s+/g, ' ');

  const results: DocSearchResult[] = [];

  for (const article of articles) {
    const entry = indexed(article);

    let score = 0;
    let matchedEveryTerm = true;

    for (const term of terms) {
      let termScore = 0;
      if (containsTerm(entry.foldedTitle, term)) termScore += 8;
      if (containsTerm(entry.foldedDescription, term)) termScore += 3;
      // Capped: an article that says "role" thirty times is about roles, but
      // it is not ten times the answer that one saying it three times is.
      termScore += Math.min(occurrences(entry.foldedBody, term), 5);

      if (termScore === 0) {
        matchedEveryTerm = false;
        break;
      }
      score += termScore;
    }

    if (!matchedEveryTerm) continue;

    if (terms.length > 1) {
      if (entry.foldedTitle.includes(phrase)) score += 25;
      else if (entry.foldedDescription.includes(phrase)) score += 8;
      else if (entry.foldedBody.includes(phrase)) score += 5;
    }
    if (entry.foldedTitle.startsWith(phrase)) score += 10;

    const snippet =
      snippetFrom(entry.body, entry.foldedBody, terms) ??
      snippetFrom(entry.description, entry.foldedDescription, terms) ??
      leadingSummary(entry.description);

    results.push({
      article,
      score,
      title: markMatches(entry.title, terms, entry.foldedTitle),
      snippet,
    });
  }

  // Sort is stable, so articles that score the same stay in reading order —
  // the order the index itself is in, which is the one a reader has already
  // been shown.
  return results.sort((a, b) => b.score - a.score);
}
